<template>
  <section class="product-specs-container">
    <div class="count-1" v-if="maxOrderAmount === 1">
      <label>Aantal </label>1
    </div>
    <counter
      v-else-if="orderProduct.volumeQuantity == 0"
      title="Aantal"
      :count="orderProduct.quantity"
      @set-count="setQuantity"
      class="spec"
    ></counter>
    <custom-specs-menu
      v-if="product.isCustomizable"
      :custom-specs="customSpecs"
      @set-custom-field="setCustomField"
    ></custom-specs-menu>

    <div v-for="(option, index) in specs" :key="index">
      <div
        class="single-spec"
        v-if="option.values && Object.keys(option.values).length === 1"
      >
        <span class="title">{{ option.title }}</span
        ><span class="spec">{{ Object.values(option.values)[0] }}</span>
      </div>
      <slider
        v-else-if="option.menuType === 'slider'"
        :options="option"
        :value="orderProduct.printSpecification[option.title]"
        @set-val="setVal"
        class="spec"
      ></slider>
      <colors-menu
        v-else-if="option.menuType === 'colors'"
        :options="option"
        :active="orderProduct.printSpecification[option.title]"
        @set-color="setColor"
        class="spec"
      ></colors-menu>
      <option-menu
        v-else-if="option.menuType === 'option'"
        :options="option"
        :active="orderProduct.printSpecification[option.title]"
        @set-val="setVal"
        @showInfo="showMaterialInfo"
        class="spec"
      ></option-menu>
      <option-menu
        v-else-if="option.menuType === 'quantity'"
        :options="option"
        :active="option.values[orderProduct.volumeQuantity]"
        @set-val="setVal"
        @showInfo="showMaterialInfo"
        class="spec"
      ></option-menu>
      <image-selector
        :options="option"
        @setImages="setImages"
        v-else-if="option.menuType === 'imageSelector'"
      >
      </image-selector>
    </div>
    <div class="single-spec" v-if="product.isStockProduct">
      <span class="title">Huidige voorraad</span
      ><span class="spec">{{ product.actualStock }}</span>
    </div>

    <section class="bottom">
      <div v-if="invalidProduct" class="invalid-order">
        De door u gekozen combinatie is helaas niet mogelijk.
      </div>

      <div class="price" v-else-if="!hidePricing">
        Prijs: {{ totalPrice }} <sup>excl. btw</sup>
      </div>

      <button
        class="pill-button"
        @click="addToCart"
        :disabled="invalidProduct || imgLoading"
      >
        In winkelwagen
      </button>
    </section>
  </section>
</template>

<script>
import Counter from '@/components/ui/Counter.vue'
import ColorsMenu from '@/components/ui/ColorsMenu.vue'
import OptionMenu from '@/components/ui/OptionMenu.vue'
import Slider from '@/components/ui/Slider.vue'
import { EventBus } from '@/eventbus/EventBus'
import PrintStoreMixin from '@/features/printstore/PrintStoreMixin'
import cloneDeep from 'lodash-es/cloneDeep'
import MaterialInfo from '../../../dialogs/StickersEnSigningMaterials.vue'
import { isEmpty } from 'lodash-es'
import CustomSpecsMenu from './productspecs/CustomSpecsMenu.vue'
import CustomProductDialog from '@/features/printstore/children/CustomProductDialog.vue'
import DialogMixin from '@/mixins/DialogMixin'
import { mapStores } from 'pinia'
import { usePriceTableStore } from '@/features/printstore/PriceTable.store'
import { usePrintStoreStore } from '@/features/printstore/PrintStore.store'
import ImageSelector from './productspecs/ImageSelector.vue'
import { toRaw } from 'vue'

export default {
  components: {
    Counter,
    OptionMenu,
    Slider,
    ColorsMenu,
    CustomSpecsMenu,
    ImageSelector
  },
  mixins: [PrintStoreMixin, DialogMixin],
  props: {
    product: Object,
    currentSpecs: Object,
    customSpecs: Object,
    imgLoading: Boolean
  },
  data() {
    return {
      orderProduct: {
        quantity: 1,
        printSpecification: {},
        variant: {},
        volumeQuantity: 0,
        customImages: []
      }
    }
  },
  computed: {
    ...mapStores(usePriceTableStore, usePrintStoreStore),
    customized() {
      return this.customSpecs
    },
    disabledSpecs() {
      if (
        this.product.productType &&
        this.product.productType.printSpecification.disabled
      ) {
        return this.product.productType.printSpecification.disabled
      } else {
        return []
      }
    },
    invalidProduct() {
      return this.disabledSpecs.indexOf(this.productNr) > -1
    },
    maxOrderAmount() {
      if (this.product.productType) {
        return this.product.productType.printSpecification.maxOrderAmount
      } else {
        return undefined
      }
    },
    specs() {
      if (this.product.productType.id) {
        let specsMenus = []
        // `products` is actually `size`

        specsMenus[0] = {
          title: this.product.productType.printSpecification.products.title,
          menuType: this.product.productType.printSpecification.products
            .menuType,
          property: 'size',
          values: this.product.productType.printSpecification.products.values
        }

        if (this.product.productType.printSpecification.colors) {
          let colors = {
            title: this.product.productType.printSpecification.colors.title,
            menuType: this.product.productType.printSpecification.colors
              .menuType,
            property: 'color',
            values: this.product.productType.printSpecification.colors.values
          }

          specsMenus.push(colors)
        }

        if (this.product.productType.printSpecification.specs) {
          this.product.productType.printSpecification.specs.forEach((spec) => {
            let propertyType
            if (spec.menuType === 'quantity') {
              propertyType = 'quantity'
            } else {
              propertyType = 'application'
            }

            specsMenus.push({
              title: spec.title,
              menuType: spec.menuType,
              property: propertyType,
              values: spec.values,
              showInfo: spec.showInfo || false
            })
          })
        }

        if (this.product.productType.printSpecification.options?.length) {
          this.product.productType.printSpecification.options.forEach(
            (option) => {
              specsMenus.push(toRaw(option))
            }
          )
        }

        return specsMenus
      } else {
        return {}
      }
    },
    productNr() {
      if (!this.product.productType.id) {
        return ''
      } else {
        const productValues = this.product.productType.printSpecification
          .products.values
        const productTitle = this.product.productType.printSpecification
          .products.title
        let selectedProduct = this.orderProduct.printSpecification[productTitle]

        let productNr = Object.keys(productValues).find(
          (key) => productValues[key] === selectedProduct
        )

        let specNrs = ''
        if (this.product.productType.printSpecification.specs) {
          this.product.productType.printSpecification.specs.forEach((spec) => {
            if (spec.menuType === 'info') {
              return
            }
            let specValues = spec.values
            let specTitle = spec.title
            //  let selectedSpec = this.orderProduct.printSpecification[specTitle]

            let specNr =
              Object.keys(specValues).find(
                (key) =>
                  specValues[key] ===
                  this.orderProduct.printSpecification[specTitle]
              ) || this.orderProduct.volumeQuantity

            specNrs += specNr
          })
        }

        return productNr + specNrs
      }
    },
    hidePricing() {
      if (
        this.hidePrice ||
        !this.priceTableStore.priceTable.hasOwnProperty(this.productNr)
      ) {
        return true
      } else if (
        this.hideOwnPrice &&
        this.product.printStoreId != this.printStoreStore.defaultPrintStoreId
      ) {
        return true
      } else {
        return false
      }
    },
    singlePrice() {
      // eslint-disable-next-line no-prototype-builtins
      if (this.priceTableStore.priceTable.hasOwnProperty(this.productNr)) {
        let singlePrice = this.priceTableStore.priceTable[this.productNr]
        return singlePrice.toFixed(2)
      } else {
        return ''
      }
    },
    totalPrice() {
      if (this.singlePrice) {
        let totalPrice = this.singlePrice * this.orderProduct.quantity
        totalPrice = totalPrice.toFixed(2)

        return '€ ' + totalPrice.replace('.', ',')
      } else {
        return ''
      }
    }
    /*     volumeQuantity() {
      if (!this.product.productType.printSpecification.specs) {
        return false
      }

      return this.product.productType.printSpecification.specs.some((spec) => {
        return spec.menuType === 'quantity'
      })
    } */
  },
  watch: {
    product(product) {
      if (product.productType) {
        this.orderProduct.printSpecification[
          product.productType.printSpecification.products.title
        ] = this.currentSpecs
          ? this.currentSpecs[
              product.productType.printSpecification.products.title
            ]
          : product.productType.printSpecification.products.values[
              product.productType.printSpecification.products.default
            ]

        if (this.product.productType.printSpecification.colors) {
          this.orderProduct.printSpecification[
            this.product.productType.printSpecification.colors.title
          ] = this.currentSpecs
            ? this.currentSpecs[
                this.product.productType.printSpecification.colors.title
              ]
            : this.product.productType.printSpecification.colors.values[
                this.product.productType.printSpecification.colors.default
              ].name
        }

        if (this.product.productType.printSpecification.specs) {
          product.productType.printSpecification.specs.forEach((spec) => {
            if (spec.menuType === 'quantity') {
              this.orderProduct.volumeQuantity = spec.default
            } else {
              this.orderProduct.printSpecification[spec.title] = this
                .currentSpecs
                ? this.currentSpecs[spec.title]
                : spec.values[spec.default]
            }
          })
        }
        if (this.currentSpecs) {
          const sizes = this.product.productType.printSpecification.products
            .values
          this.orderProduct.variant.size = Object.keys(sizes).find(
            (key) =>
              sizes[key] ===
              this.currentSpecs[
                this.product.productType.printSpecification.products.title
              ]
          )

          if (this.product.productType.printSpecification.specs) {
            const applications = this.product.productType.printSpecification
              .specs[0].values
            this.orderProduct.variant.application = Object.keys(
              applications
            ).find(
              (key) =>
                applications[key] ===
                this.currentSpecs[
                  this.product.productType.printSpecification.specs[0].title
                ]
            )
          }
          if (this.product.productType.printSpecification.colors) {
            const colors = this.product.productType.printSpecification.colors
              .values
            this.orderProduct.variant.color = Object.keys(colors).find(
              (key) =>
                colors[key].name ===
                this.currentSpecs[
                  this.product.productType.printSpecification.colors.title
                ]
            )
          }
        } else {
          if (this.product.productType.printSpecification.specs) {
            this.orderProduct.variant.application = this.product.productType.printSpecification.specs[0].default
          }
          this.orderProduct.variant.size = this.product.productType.printSpecification.products.default

          if (this.product.productType.printSpecification.colors) {
            this.orderProduct.variant.color = this.product.productType.printSpecification.colors.default
          }
        }
        //}

        this.orderProduct.name = this.product.name
        this.orderProduct.image = this.product.image
        this.orderProduct.previewImage = this.product.previewImage
        this.orderProduct.id = this.product.id
        this.orderProduct.collectionId = this.$route.params.collectionId
        this.orderProduct.categoryId = this.$route.params.categoryId
        this.orderProduct.printStoreId = this.product.printStoreId
        this.orderProduct.price = this.singlePrice
        this.orderProduct.productCode = this.productNr
        this.orderProduct.product = this.product.productType.printSpecification.title
        this.orderProduct.productTypeId = this.product.productTypeId
      }
    }
  },
  methods: {
    setCustomField(values) {
      this.$emit('set-custom-field', values)
    },
    setQuantity(count) {
      this.orderProduct.quantity = parseInt(count)
    },
    setVal(title, val, property, value) {
      this.updated = true
      this.orderProduct.variant[property] = value

      if (property === 'quantity') {
        this.orderProduct.volumeQuantity = value
      } else {
        this.orderProduct.printSpecification[title] = val
      }
    },
    setColor(title, label, property, value) {
      this.updated = true
      this.orderProduct.printSpecification[title] = label
      this.orderProduct.variant[property] = value
      this.$emit('set-color', value)
    },
    setImages(images) {
      const existingIndex = this.orderProduct.customImages.findIndex(
        (item) => item.title === images.title
      )
      if (existingIndex !== -1) {
        this.orderProduct.customImages[existingIndex] = images
      } else {
        this.orderProduct.customImages.push(images)
      }
    },
    addToCart() {
      this.orderProduct.productCode = this.productNr

      if (this.product.isCustomizable) {
        this.orderProduct.isCustomizable = true
        this.orderProduct.customFields = this.customSpecs.fieldData
        this.orderProduct.previewImage = this.product.previewImage
        this.orderProduct.customProductId = parseInt(
          this.product.customVariants[this.orderProduct.variant.size]
        )
      }

      if (this.product.hasVariants && !isEmpty(this.product.previews)) {
        if (this.product.productType.printSpecification.colors) {
          this.orderProduct.previewImage = this.product.previews.find(
            (img) => img.colour === this.orderProduct.variant.color
          ).uuid
        } else {
          this.orderProduct.previewImage = this.product.previews[0].uuid
        }
      }

      if (this.orderProduct.customImages.length === 0) {
        delete this.orderProduct.customImages
      }
      if (this.product.divisionCode) {
        this.orderProduct.divisionCode = this.product.divisionCode
      }
      this.orderProduct.price = this.singlePrice
      this.orderProduct.isStockProduct = this.product.isStockProduct

      const product = cloneDeep(this.orderProduct)

      this.printStoreStore.addToCart(product)

      this.$router.push({
        name: 'printStoreCollection',
        params: {
          categoryId: this.$route.params.categoryId,
          collectionId: this.$route.params.collectionId
        }
      })

      EventBus.emit('addToBasket')
    },
    showMaterialInfo() {
      this.$modal.show(
        MaterialInfo,
        {},
        {
          minWidth: 380,
          width: '100%',
          height: 'auto',
          maxWidth: 940,
          adaptive: true,
          scrollable: true
        }
      )
    }
  }
}
</script>

<style lang="scss" scoped>
@use '../../../sass/base/_colors.scss' as *;

.product-specs-container {
  background-color: #f4f4f4;
  padding: 20px;
  border-radius: 8px;
}

h2 {
  font-weight: 500;
  color: $text-color;
  margin-bottom: 12px;
  font-size: 13px;
}

.product {
  padding: 10px 0;
}

.spec {
  padding: 6px 0;
}

.bottom {
  display: flex;
  margin-top: 30px;
  justify-content: space-between;

  .pill-button {
    margin-left: auto;
    white-space: nowrap;
  }
}

.price {
  padding: 5px 0;
  font-weight: 500;
  font-size: 14px;
}

.single-spec {
  line-height: 15px;
  padding: 15px 0;

  .title {
    font-weight: 500;
  }

  .spec {
    margin-left: 6px;
  }
}

.invalid-order {
  color: var(--error-color);
  font-weight: 500;
  padding-right: 10px;
}
</style>
